import {testIdProps} from '@github-ui/test-id-props'
import {ValidationErrorPopover} from '@github-ui/validation-error-popover'
import {ActionList, type ActionListItemProps, ActionMenu, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useMemo} from 'react'

import {getDisplayUnits, type IterationDuration, type IterationDurationUnits} from '../../../helpers/iterations'
import {usePrefixedId} from '../../../hooks/common/use-prefixed-id'
import {Resources} from '../../../strings'
import styles from './iteration-duration-input.module.css'

export interface ValidationProps {
  /** When false, validation UI is suppressed. Defaults to true. */
  showValidationErrors?: boolean
  /**
   * How validation should be presented.
   * - 'inline' will render a FormControl.Validation (parent must provide the message).
   * - 'popover' will render a popover (child will show its own message).
   * Defaults to 'popover'.
   */
  validationStyle?: 'inline' | 'popover'
  /**
   * Optional callback invoked when validity changes.
   * Receives (isValid: boolean, message?: string).
   * Parent components can use the message to render inline validation UI when `validationStyle` is 'inline'.
   */
  onChange?: (isValid: boolean, message?: string) => void
  /** Optional validation message to show when `validationStyle` is 'inline'. */
  validationMessage?: string
}

const DefaultValidation: ValidationProps = {
  showValidationErrors: true,
  validationStyle: 'popover',
}

interface IterationDurationInputProps {
  /** The current value of the input. */
  value: IterationDuration
  /** Called when the value changes. */
  onChange: (value: IterationDuration) => void
  /** When true uses compact layout for unit controls */
  compactLayout?: boolean
  /** Optional validation configuration for the input. */
  validation?: ValidationProps
}

type UnitItemInput = ActionListItemProps & {key: IterationDurationUnits}
const allUnits = ['days', 'weeks'] as const
function buildUnitItemsMap(quantity: number): Map<IterationDurationUnits, UnitItemInput> {
  return new Map(
    allUnits.map(u => [
      u,
      {
        children: getDisplayUnits(quantity, u),
        key: u,
        ...testIdProps(`duration-${u}-menu-item`),
      },
    ]),
  )
}

const MIN_QTY = 1
const MAX_QTY = 99

interface QuantityValidity {
  isValid: boolean
  message?: string
}

/** Validate a duration quantity value. Returns an error message or `null` if the value is valid. */
// It would be more clear to return a {isValid, message} object here, but that's much
// more difficult to manage when it comes to avoiding extra re-renders.
function validateQuantity(quantity: number): QuantityValidity {
  if (Number.isNaN(quantity)) {
    return {isValid: false, message: Resources.iterationDurationQtyCannotBeBlank}
  }
  if (quantity < MIN_QTY) return {isValid: false, message: Resources.iterationDurationQtyLow(MIN_QTY)}
  if (quantity > MAX_QTY) return {isValid: false, message: Resources.iterationDurationQtyHigh(MAX_QTY)}
  return {isValid: true}
}

/** Ignore non-numeric digits, like scientific notation and decimals */
function ignoreNonDigitNumberChars(event: React.KeyboardEvent<HTMLInputElement>): void {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  switch (event.key) {
    case 'e':
    case '-':
    case '.':
    case '+':
      event.preventDefault()
      break
  }
}

/**
 * Component to input quantity and units for an iteration's duration. Handles validation -
 * listen to the `onValidChange` event and disallow submitting unless valid.
 */
export function IterationDurationInput({
  value,
  onChange,
  compactLayout = false,
  validation,
}: IterationDurationInputProps) {
  // eslint-disable-next-line react-hooks/immutability
  validation = {...DefaultValidation, ...validation}

  const qtyValidity = useMemo(() => validateQuantity(value.quantity), [value.quantity])

  // This has the unfortunate effect of calling onValidChange every time it changes. Fortunately it's only called with a
  // boolean so calling again with the same value should be OK. If it becomes a problem we can revisit or just memoize the handler.
  useEffect(() => {
    validation?.onChange?.(qtyValidity.isValid, qtyValidity.message)
  }, [validation?.onChange, qtyValidity.isValid, qtyValidity.message, validation])

  const showValidation = validation.showValidationErrors
  const validationStyle = validation.validationStyle
  const showInline = showValidation && validationStyle === 'inline' && !qtyValidity.isValid
  const showPopover = showValidation && validationStyle === 'popover' && !!qtyValidity.message

  const onQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const quantity = e.target.valueAsNumber
    // We don't have to update validity here because it will update by effect when the parent updates value.quantity
    onChange({...value, quantity})
  }

  const onUnitsChange = (item?: ActionListItemProps) => {
    if (item) onChange({...value, units: (item as UnitItemInput).key})
  }

  const groupLabelId = usePrefixedId('iteration-duration')
  const qtyValidationMessageId = usePrefixedId('duration-quantity-validation-message')

  const unitItems = useMemo(() => {
    const map = buildUnitItemsMap(value.quantity)
    return Array.from(map.values())
  }, [value.quantity])

  return (
    <div
      className={styles.DurationContainer}
      role="group"
      aria-label={Resources.iterationDurationLabel}
      aria-labelledby={groupLabelId}
    >
      <div className={styles.QuantityInputContainer}>
        <TextInput
          type="number"
          min={MIN_QTY}
          max={MAX_QTY}
          step={1}
          required
          value={Number.isNaN(value.quantity) ? '' : value.quantity}
          onChange={onQtyChange}
          onKeyDown={ignoreNonDigitNumberChars}
          aria-invalid={showInline || showPopover}
          aria-describedby={showInline || (showPopover && qtyValidity.message) ? qtyValidationMessageId : undefined}
          aria-label="Duration quantity"
          className={clsx(styles.QuantityInput, {[styles.Error]: showInline || showPopover})}
          {...testIdProps('duration-quantity-input')}
          id="duration-quantity-input"
        />

        {showPopover ? (
          <ValidationErrorPopover
            message={qtyValidity.message}
            id={qtyValidationMessageId}
            testId="duration-quantity-validation-message"
          />
        ) : null}
      </div>

      <ActionMenu>
        <ActionMenu.Button
          className={compactLayout ? styles.UnitsButtonCompact : styles.UnitsButton}
          aria-label="Units"
          {...testIdProps('duration-units-button')}
        >
          <span className={styles.UnitsText} {...testIdProps('duration-units-value')}>
            {value.units}
          </span>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList selectionVariant="single">
            {unitItems.map(unitItem => {
              const {key, ...rest} = unitItem
              return (
                <ActionList.Item
                  key={key}
                  {...rest}
                  selected={key === value.units}
                  onSelect={() => onUnitsChange(unitItem)}
                />
              )
            })}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}
